import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-data-grid';
import '@vitesse/base-ui/elements/inputs/vitesse-select';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';
import { t, formatNumber } from '@vitesse/base-ui/i18n';
import { getSession } from '@vitesse/base-ui';
import { button, baseButtonStyles } from '@vitesse/base-ui/components/base-buttons';
import '@vitesse/base-ui/elements/vitesse-gated';
import { Integrations } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import type { DataGridColumn, DataGridPage } from '@vitesse/base-ui/elements/vitesse-data-grid';
import type { BackendClient } from '../../bridge/backend-client';
import type { RowFilterOp, RowPredicateLite } from '../../../core/bridge/connect-client';
import { SEARCH_DEBOUNCE_MS } from '../../../core/ui-constants';
import { tableSource, type TableSource, type TableColumn } from './integration-sources';
import { mayReadValues } from './integration-access';
import { editableColumns, buildRowWhere, coerceCell, isChanged } from './table-row-edit';
import { showResultError } from '../../errors';
import {
  loadFilters, dumpFilters, upsertFilter, removeFilter, filtersFor, validName, storeKey,
  type SavedFilter,
} from './table-saved-filters';

/*
 * <vitesse-table-viewport> — the integration "table" view (SQL / warehouse), DataGrip-
 * shaped: a NAVIGATOR (left) + a DATA GRID (right). BOTH page the connector on demand
 * — NOTHING is materialized, so it scales to millions of tables × billions of rows:
 *
 *   • Navigator = a windowed, searchable, paged list of TABLES via BrowseSchema
 *     (server queries information_schema for just the viewport; the browser slides a
 *     bounded window). A table's COLUMNS load lazily on select (BrowseColumns).
 *   • Data grid = the selected table's ROWS via keyset Read — O(log n) at any depth,
 *     bounded sliding window.
 *
 * The browser never holds the whole schema or the whole table — only the viewport of
 * each (the symbol paradigm, a layer lower: query exactly what's on screen, cache
 * nothing). Both panes reuse the same <vitesse-data-grid> windowed scroller.
 */

const NAV_COLS: DataGridColumn[] = [{ key: 'name', label: 'Tables' }];

/** Comparison ops rendered as their SQL-ish symbol (universal, untranslated);
 *  the word-shaped ops (contains / null tests) go through the locale files. */
const OP_SYMBOL: Partial<Record<RowFilterOp, string>> = {
  eq: '=',
  neq: '≠',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
};

function opLabel(op: RowFilterOp): string {
  switch (op) {
    case 'contains':
      return t('integration.filterOpContains');
    case 'isNull':
      return t('integration.filterOpIsNull');
    case 'notNull':
      return t('integration.filterOpNotNull');
    default:
      return OP_SYMBOL[op] ?? op;
  }
}

@customElement('vitesse-table-viewport')
export class TableViewport extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  @property() integrationId = '';

  @state() private selected = '';
  @state() private navSearch = '';
  @state() private dataCols: TableColumn[] = [];
  /** The columns that identify one row of the selected table, from the page reply.
   *  Empty = this table cannot be edited in place (no key, or a view), and no edit
   *  affordance is offered — the gated-UI rule. */
  @state() private rowKey: string[] = [];
  @state() private colsLoading = false;
  // Bumped to force the table LIST to re-query after a troubled initial load (its
  // browse may have raced empty too) — folded into the nav grid's resetKey.
  @state() private navRefreshKey = 0;
  // The Refresh button bumps this. It is part of the rows grid's resetKey, so the grid reads the
  // table again from the first page. Without it, the rows changed only with the table or the filter.
  @state() private rowsRefreshKey = 0;

  // ── Row filter (WHERE-chip bar) — server-side predicate pushdown ──────────
  // The filter travels on Integrations.Read (exact-or-refuse: the service
  // refuses in prose on a source without pushdown, never silently ignores it).
  // Cursors are filter-scoped, so the filter signature is folded into the data
  // grid's resetKey — every filter change restarts from the first page.
  @state() private filter: RowPredicateLite[] = [];
  /** From the last page reply: does this source honour filters? undefined =
   *  not known yet (first page hasn't answered); false hides the bar entirely —
   *  the SOURCE has no such concept, and per the gated-UI rule a control with
   *  no path to it is absent, not present-and-broken. */
  @state() private filterable?: boolean;
  /** True row count under the current filter, when the source can answer
   *  cheaply. undefined = unknown → render NOTHING (absent is not zero). */
  @state() private totalRows?: number;
  /** The server's refusal/read error, surfaced verbatim (it arrives in prose). */
  @state() private filterError = '';
  // "+ filter" editor state.
  @state() private addOpen = false;
  @state() private newCol = '';
  @state() private newOp: RowFilterOp = 'eq';
  @state() private newVal = '';
  // ── Saved filters ─────────────────────────────────────────────────────────
  // The filter machinery already pushed down; what was missing was KEEPING one.
  // Per browser (localStorage), matching the endpoint viewport's selection
  // memory — see table-saved-filters.ts for why that limit is deliberate.
  @state() private saved: SavedFilter[] = [];
  @state() private saveOpen = false;
  @state() private saveName = '';

  private searchTimer?: number;
  private retryTimer?: number;
  private initialTries = 0;
  // Whether the navigator's FIRST page came back with rows — gates the recovery
  // refresh below: a populated, possibly-scrolled list is never reset (5a).
  private navHasRows = false;
  // The source seam (Cardinal Rule: this component never calls the transport
  // itself) — rebuilt when the gateway/integration identity changes.
  private src?: TableSource;
  private srcFor = '';
  // Re-entrancy guard: firstUpdated AND updated (integrationId is in `changed` on
  // first set) both call loadInitial on first render — without this they'd run two
  // concurrent browse+retry chains (the 2nd overwrites retryTimer, orphaning the 1st).
  private loadingInitial = false;
  // Re-render on a session change so a grant or revocation of integration:read
  // moves the data pane without a reload (the "revoked mid-session" cell of the
  // gating matrix). The decision itself is read fresh at render.
  private sessionOff?: () => void;

  // baseButtonStyles FIRST: this element has a shadow root, and `button()` emits only class names.
  // The global base-button sheet cannot cross that boundary, so without it the Refresh button
  // renders as a bare browser button (endpoint-viewport.ts has the same note).
  static styles = [
    baseButtonStyles,
    css`
    :host {
      display: flex;
      box-sizing: border-box;
      min-height: 360px;
      border: 1px solid var(--border-color);
      border-radius: var(--radius, 8px);
      overflow: hidden;
      background: var(--bg-primary);
    }
    .nav {
      width: 260px;
      flex: none;
      border-right: 1px solid var(--border-color);
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    .nav-search {
      flex: none;
      padding: 8px;
      border-bottom: 1px solid var(--border-color);
    }
    .nav-search input {
      width: 100%;
      box-sizing: border-box;
      padding: 6px 10px;
      font-size: 13px;
      color: var(--text-primary);
      background: var(--bg-secondary, var(--hover-bg));
      border: 1px solid var(--border-color);
      border-radius: var(--radius, 6px);
      outline: none;
    }
    .nav-search input:focus {
      border-color: var(--blue);
    }
    vitesse-data-grid {
      flex: 1;
      min-height: 0;
    }
    .data {
      flex: 1;
      min-width: 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .data-head {
      padding: 9px 14px;
      display: flex;
      align-items: baseline;
      gap: 10px;
      border-bottom: 1px solid var(--border-color);
    }
    .tname {
      font-weight: 600;
      color: var(--text-primary);
    }
    .meta {
      font-size: 12px;
      color: var(--text-secondary);
    }
    /* The editability note sits quieter than the table name and column count —
       it is an availability hint, not a status. */
    .meta.edit-hint {
      color: var(--text-tertiary, var(--sub-text, var(--text-secondary)));
    }
    .head-actions {
      margin-left: auto;
      align-self: center;
    }
    .empty {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 28px 14px;
      text-align: center;
      color: var(--text-secondary);
      font-size: 13px;
    }
    /* WHERE-chip filter bar — the dev-harness table scene's design language. */
    .filter-bar {
      flex: none;
      padding: 7px 14px;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      border-bottom: 1px solid var(--border-color);
    }
    .filter-kw {
      font-size: 11px;
      color: var(--text-tertiary, var(--text-secondary));
      font-family: var(--font-mono, monospace);
    }
    .filter-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 3px 8px;
      font-size: 12px;
      border-radius: 5px;
      background: color-mix(in srgb, var(--blue) 10%, transparent);
      color: var(--blue);
      font-family: var(--font-mono, monospace);
    }
    .filter-chip button {
      all: unset;
      cursor: pointer;
      opacity: 0.6;
      line-height: 1;
    }
    .filter-chip button:hover {
      opacity: 1;
    }
    /* A SAVED filter, visually distinct from an ACTIVE predicate above: active
       chips are blue and describe the query running right now; saved ones are
       neutral and are things you can invoke. Same shape so they read as the same
       family, different weight so they are not mistaken for each other. */
    .filter-saved {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 3px 8px;
      font-size: 12px;
      border-radius: 5px;
      border: 1px dashed var(--border-color);
      color: var(--text-secondary);
    }
    .filter-saved button {
      all: unset;
      cursor: pointer;
      opacity: 0.6;
      line-height: 1;
    }
    .filter-saved button:hover {
      opacity: 1;
    }
    .filter-saved-apply {
      opacity: 1 !important;
      color: var(--text-primary);
    }
    .filter-saved-apply:hover {
      color: var(--blue);
    }
    .filter-add[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .filter-add {
      padding: 3px 8px;
      font-size: 12px;
      border: 1px dashed var(--border-color);
      border-radius: 5px;
      background: transparent;
      color: var(--text-secondary);
      cursor: pointer;
      font-family: var(--font, inherit);
    }
    .filter-add:hover {
      color: var(--text-primary);
      border-color: var(--blue);
    }
    .filter-editor {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .filter-editor vitesse-select {
      min-width: 110px;
    }
    .filter-editor vitesse-text-input {
      width: 120px;
    }
    .filter-count {
      margin-left: auto;
      font-size: 11px;
      color: var(--text-tertiary, var(--text-secondary));
      font-family: var(--font-mono, monospace);
    }
    .filter-error {
      flex: none;
      padding: 6px 14px;
      font-size: 12px;
      color: var(--red, #d64541);
      border-bottom: 1px solid var(--border-color);
    }
  `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('resize', this.fitHeight);
    this.sessionOff = getSession()?.onChange(() => this.requestUpdate());
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.fitHeight);
    this.sessionOff?.();
    this.sessionOff = undefined;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }
  protected firstUpdated(): void {
    this.fitHeight();
    void this.loadInitial();
  }
  protected updated(changed: Map<string, unknown>): void {
    // The integration id can change while the element is reused — re-load from scratch.
    if (changed.has('integrationId')) {
      this.selected = '';
      this.dataCols = [];
      this.initialTries = 0;
      this.navHasRows = false; // the new integration's navigator hasn't answered yet
      this.loadingInitial = false; // drop any in-flight guard from the previous id so the new load isn't blocked
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = undefined;
      }
      void this.loadInitial();
    }
  }

  /** The table screen's source object, keyed to the current gateway+integration
   *  so a reused element gets a fresh one when its identity changes. */
  private get tableSrc(): TableSource | undefined {
    if (!this.backend) return undefined;
    const key = `${this.gatewayId}|${this.integrationId}`;
    if (!this.src || this.srcFor !== key) {
      this.src = tableSource(this.backend, this.gatewayId, this.integrationId);
      this.srcFor = key;
      // Saved filters are per integration, so a new identity means a new set.
      this.readSaved();
    }
    return this.src;
  }

  // Lock host height to the viewport (the detail-element tag isn't height-bounded; the
  // grid's internal overflow then has a bounded box to scroll).
  private fitHeight = (): void => {
    const top = this.getBoundingClientRect().top;
    this.style.height = `${Math.max(360, Math.round(window.innerHeight - top - 24))}px`;
  };

  /** Show data immediately by selecting the first table (one tiny browse). The initial
   *  browse can come back empty or fail transiently — the connector may still be
   *  completing its connection, or a cloud leaf re-sync is in flight — so retry with
   *  bounded backoff until the schema is queryable. This is the one-shot-browse analog
   *  of the value tree's stream re-snapshot: both recover when the data shows up late,
   *  but the navigator queries on demand (nothing materialized) so it can't rely on a
   *  pushed snapshot. */
  private async loadInitial(): Promise<void> {
    const src = this.tableSrc;
    if (!src || this.selected || this.loadingInitial) return;
    this.loadingInitial = true;
    const myId = this.integrationId;
    let firstPath: string | undefined;
    try {
      firstPath = await src.probeFirstTable();
    } catch {
      /* transient — fall through to the retry below */
    }
    this.loadingInitial = false; // cleared post-await so a scheduled retry (or id change) can re-enter
    if (this.integrationId !== myId || this.selected) return; // raced / changed mid-await

    if (firstPath) {
      // The list browse (a separate call) may have raced empty in the same window — once a
      // troubled load recovers, re-query it so the navigator isn't left showing "No rows".
      // ONLY when it actually IS empty: a populated list the user may have scrolled must
      // never be yanked back to the top by a background recovery.
      if (this.initialTries > 0 && !this.navHasRows) this.navRefreshKey++;
      void this.selectTable(firstPath);
      return;
    }
    // Empty or failed: retry with bounded backoff (~30s total), then settle on the
    // "No rows" state — a genuinely empty schema or a connector that can't connect.
    if (this.initialTries < 12) {
      const delay = Math.min(3000, 300 * 2 ** this.initialTries);
      this.initialTries++;
      this.retryTimer = window.setTimeout(() => {
        this.retryTimer = undefined;
        if (this.integrationId === myId && !this.selected) void this.loadInitial();
      }, delay);
    }
  }

  // Navigator source — pages the table list (search-scoped). The grid asks for
  // exactly `limit` = one viewport's worth; it slides a bounded window itself.
  private navSource = async (cursor: string, limit: number): Promise<DataGridPage> => {
    const src = this.tableSrc;
    if (!src) return { rows: [], nextCursor: '', prevCursor: '' };
    const page = await src.tables(cursor, this.navSearch, limit);
    if (cursor === '') this.navHasRows = page.rows.length > 0;
    return page;
  };

  // Data source — keyset-paged rows of the selected table. `limit` is the grid's
  // viewport-derived page size; the browser never holds more than a few screens.
  // The active filter rides every page; the reply's capability + count land in
  // state (change-guarded, so a steady page stream doesn't re-render the bar).
  private dataSource = async (cursor: string, limit: number): Promise<DataGridPage> => {
    const src = this.tableSrc;
    if (!src || !this.selected) return { rows: [], nextCursor: '', prevCursor: '' };
    try {
      const page = await src.rows(this.selected, cursor, limit, this.filter.length > 0 ? this.filter : undefined);
      if (page.filterable !== this.filterable) this.filterable = page.filterable;
      if (page.totalRows !== this.totalRows) this.totalRows = page.totalRows;
      if (page.rowKey.join() !== this.rowKey.join()) this.rowKey = page.rowKey;
      if (this.filterError) this.filterError = '';
      return { rows: page.rows, nextCursor: page.nextCursor, prevCursor: page.prevCursor };
    } catch (err) {
      // The refusal is already prose (the universal Result envelope) — show it
      // verbatim above the grid rather than swallowing it.
      this.filterError = err instanceof Error ? err.message : String(err);
      return { rows: [], nextCursor: '', prevCursor: '' };
    }
  };

  /** The filter's identity for the grid's resetKey: a filter change restarts
   *  paging from cursor "" (cursors are filter-scoped on the wire). */
  private get filterSig(): string {
    return this.filter.length === 0 ? '' : JSON.stringify(this.filter);
  }

  /** Coerce the editor's raw text by the column's declared SQL type, so a
   *  numeric column compares numerically (bound as a number server-side). */
  private coerceValue(col: string, raw: string): string | number | boolean {
    const type = (this.dataCols.find((c) => c.key === col)?.type ?? '').toLowerCase();
    if (/int|serial|real|float|double|numeric|decimal/.test(type)) {
      const n = Number(raw);
      if (raw.trim() !== '' && !Number.isNaN(n)) return n;
    }
    if (/bool/.test(type)) {
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    }
    return raw;
  }

  private addPredicate(): void {
    const col = this.newCol || this.dataCols[0]?.key;
    if (!col) return;
    const needsValue = this.newOp !== 'isNull' && this.newOp !== 'notNull';
    if (needsValue && this.newVal === '') return;
    const p: RowPredicateLite = needsValue
      ? { column: col, op: this.newOp, value: this.coerceValue(col, this.newVal) }
      : { column: col, op: this.newOp };
    this.filter = [...this.filter, p];
    this.filterError = '';
    this.addOpen = false;
    this.newVal = '';
  }

  // ── Saved filters ─────────────────────────────────────────────────────────
  private readSaved(): void {
    try {
      this.saved = loadFilters(localStorage.getItem(storeKey(this.integrationId)));
    } catch {
      this.saved = []; // private window / storage disabled — no memory, not a crash
    }
  }

  private writeSaved(next: SavedFilter[]): void {
    this.saved = next;
    try {
      localStorage.setItem(storeKey(this.integrationId), dumpFilters(next));
    } catch {
      /* quota or disabled storage: the in-memory list still works for this visit */
    }
  }

  private saveCurrentFilter(): void {
    if (!validName(this.saveName) || this.filter.length === 0 || !this.selected) return;
    this.writeSaved(upsertFilter(this.saved, { name: this.saveName.trim(), table: this.selected, predicates: this.filter }));
    this.saveName = '';
    this.saveOpen = false;
  }

  /** Applying replaces the whole predicate set rather than merging: a saved view
   *  is "show me exactly this", and merging would silently produce a filter the
   *  user never saved and cannot see the provenance of. */
  private applySaved(f: SavedFilter): void {
    this.filter = f.predicates.map((p) => ({ ...p }));
    this.filterError = '';
    this.totalRows = undefined;
  }

  private removePredicate(i: number): void {
    this.filter = this.filter.filter((_, idx) => idx !== i);
    this.filterError = '';
  }

  private async selectTable(name: string): Promise<void> {
    if (name === this.selected && this.dataCols.length) return;
    this.selected = name;
    this.dataCols = [];
    // A filter names THIS table's columns — it cannot carry over to another.
    this.filter = [];
    this.filterError = '';
    this.totalRows = undefined;
    this.rowKey = [];
    this.addOpen = false;
    this.colsLoading = true;
    try {
      // Lazy-load just THIS table's columns (the grid headers).
      const cols = await this.tableSrc!.columns(name);
      if (this.selected !== name) return; // selection changed mid-load
      this.dataCols = cols;
    } catch {
      this.dataCols = [];
    } finally {
      if (this.selected === name) this.colsLoading = false;
    }
  }

  /* ── in-cell editing ──────────────────────────────────────────────────────
   *
   * An edit changes a row in the customer's database, so what the grid may offer
   * is decided here rather than there: only when the page reply named the row's
   * key columns, and only on columns that are neither part of that key nor
   * read-only. See table-row-edit.ts for the rules and why each one exists.
   */
  private get editable(): string[] {
    return [...editableColumns(this.dataCols, this.rowKey)];
  }

  /** A row's identity for the grid, so an open editor stays attached to its ROW
   *  while the window scrolls and re-indexes beneath it. */
  private readonly rowIdOf = (row: Record<string, unknown>): string =>
    this.rowKey.map((k) => String(row[k] ?? '\u0000')).join('\u001f');

  /** Persist one cell. Rejecting is meaningful: the grid reverts the cell it had
   *  painted, so a refusal never leaves a value on screen that was never stored. */
  private readonly commitCell = async (
    row: Record<string, string | number | boolean | null | undefined>,
    column: string,
    raw: string,
  ): Promise<string | number | boolean | null> => {
    const src = this.tableSrc;
    if (!src || !this.selected) throw new Error('no table selected');

    const next = coerceCell(raw, this.dataCols.find((c) => c.key === column)?.type);
    // Typing the same thing back is not a write. A no-op UPDATE still has to match
    // exactly one row, so sending it could fail for a reason nobody could act on.
    if (!isChanged(row[column], next)) return next;

    const where = buildRowWhere(row, this.rowKey, column);
    if (where.length === 0) {
      // The row cannot be addressed — a null key value, or a key column missing
      // from the page. Refuse rather than send a predicate that would widen.
      const err = new Error(t('integration.editUnaddressable'));
      showResultError(err, t('integration.editRefused'));
      throw err;
    }
    try {
      await src.updateRow(this.selected, where, { [column]: next });
      // The TYPED value, not the raw text: the grid writes this back into its row
      // so a second edit of the cell is built against what the source now holds.
      return next;
    } catch (err) {
      // The source's own prose — which row, which column, and whether it changed
      // underneath — is better than anything we could reword.
      showResultError(err, t('integration.editRefused'));
      throw err;
    }
  };

  private onSearchInput(e: Event): void {
    const v = (e.target as HTMLInputElement).value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => {
      this.navSearch = v; // changes the nav grid's resetKey → re-query from the first page
    }, SEARCH_DEBOUNCE_MS);
  }

  protected render() {
    return html`
      <div class="nav">
        <div class="nav-search">
          <input type="text" placeholder="Search tables…" @input=${(e: Event) => this.onSearchInput(e)} />
        </div>
        <vitesse-data-grid
          .columns=${NAV_COLS}
          .source=${this.navSource}
          .resetKey=${`tables:${this.navSearch}:${this.navRefreshKey}`}
          rowKey="name"
          .selectedKey=${this.selected}
          @row-click=${(e: CustomEvent<{ key: string }>) => void this.selectTable(e.detail.key)}
        ></vitesse-data-grid>
      </div>
      <div class="data">${this.renderData()}</div>
    `;
  }

  private renderData() {
    if (!this.selected) return html`<div class="empty">Select a table to view its data.</div>`;
    return html`
      <div class="data-head">
        <span class="tname">${this.selected}</span>
        <span class="meta">${this.colsLoading ? 'loading columns…' : `${this.dataCols.length} cols`}</span>
        ${
          // Say WHY editing is or is not on offer. Absent-and-unexplained reads as
          // a missing feature; this is the same courtesy the filter bar extends by
          // hiding itself only when the source genuinely cannot filter.
          this.colsLoading || !this.dataCols.length
            ? ''
            : this.editable.length
              ? html`<span class="meta edit-hint">${t('integration.editHint')}</span>`
              : html`<span class="meta" title=${t('integration.editReadOnly')}>${t('integration.editReadOnly')}</span>`
        }
        ${
          // Only where the rows grid is mounted (see renderRows): a refresh of rows the caller may not read does nothing.
          mayReadValues(getSession())
            ? html`<span class="head-actions" title=${t('integration.tableRefreshHint')}
                >${button({
                  label: t('integration.tableRefresh'),
                  icon: 'refresh-cw',
                  size: 'sm',
                  disabled: this.colsLoading,
                  onClick: () => this.rowsRefreshKey++,
                })}</span
              >`
            : ''
        }
      </div>
      ${this.renderFilterBar()}
      ${this.filterError ? html`<div class="filter-error">${this.filterError}</div>` : ''}
      ${this.renderRows()}
    `;
  }

  /** The rows grid — the one Read in this viewport. The navigator (tables and
   *  columns) is BrowseSchema and stays; a caller with browse but not read
   *  sees the table's name and columns with a lock where its rows would be.
   *  The grid is not MOUNTED when refused, not merely hidden: mounting it would
   *  fire the Read the lock exists to prevent. <vitesse-gated> renders the lock
   *  and its reason from the same annotation the server enforces. */
  private renderRows() {
    const grid = this.colsLoading
      ? html`<div class="empty">Loading…</div>`
      : html`<vitesse-data-grid
          .columns=${this.dataCols}
          .source=${this.dataSource}
          .resetKey=${`${this.selected}|${this.filterSig}|${this.rowsRefreshKey}`}
          .editableColumns=${this.editable}
          .rowId=${this.rowIdOf}
          .commitCell=${this.commitCell}
        ></vitesse-data-grid>`;
    return html`<vitesse-gated .rpc=${Integrations.method.read}>${mayReadValues(getSession()) ? grid : ''}</vitesse-gated>`;
  }

  /** The WHERE-chip bar. Rendered only when the source is KNOWN to honour
   *  filters (or one is already set): a source without pushdown has no path to
   *  this control, so it is absent — never present-and-refusing. */
  private renderFilterBar() {
    if (this.colsLoading || !(this.filterable === true || this.filter.length > 0)) return '';
    const ops: RowFilterOp[] = ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'contains', 'isNull', 'notNull'];
    const needsValue = this.newOp !== 'isNull' && this.newOp !== 'notNull';
    // Only those saved against the table on screen — a predicate names columns,
    // and offering one built for another table would produce a refusal.
    const saved = filtersFor(this.saved, this.selected);
    return html`<div class="filter-bar">
      <span class="filter-kw">WHERE</span>
      ${this.filter.map(
        (p, i) => html`<span class="filter-chip"
          >${p.column} ${opLabel(p.op)}${p.value !== undefined ? html` ${String(p.value)}` : ''}
          <button title=${t('integration.filterRemove')} @click=${() => this.removePredicate(i)}>✕</button></span
        >`,
      )}
      ${
        this.addOpen
          ? html`<span class="filter-editor">
              <vitesse-select
                size="sm"
                .options=${this.dataCols.map((c) => ({ value: c.key, label: c.key }))}
                .value=${this.newCol || this.dataCols[0]?.key || ''}
                @change=${(e: CustomEvent<{ value: string }>) => (this.newCol = e.detail.value)}
              ></vitesse-select>
              <vitesse-select
                size="sm"
                .options=${ops.map((o) => ({ value: o, label: opLabel(o) }))}
                .value=${this.newOp}
                @change=${(e: CustomEvent<{ value: string }>) => (this.newOp = e.detail.value as RowFilterOp)}
              ></vitesse-select>
              ${
                needsValue
                  ? html`<vitesse-text-input
                      size="sm"
                      placeholder=${t('integration.filterValue')}
                      .value=${this.newVal}
                      @change=${(e: CustomEvent<{ value: string }>) => (this.newVal = e.detail.value)}
                      @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.addPredicate()}
                    ></vitesse-text-input>`
                  : ''
              }
              <button class="filter-add" @click=${() => this.addPredicate()}>${t('integration.filterApply')}</button>
            </span>`
          : html`<button class="filter-add" @click=${() => (this.addOpen = true)}>+ ${t('integration.filterAdd')}</button>`
      }
      ${
        this.filter.length > 0
          ? this.saveOpen
            ? html`<span class="filter-editor">
                <vitesse-text-input
                  size="sm"
                  placeholder=${t('integration.filterSaveName')}
                  .value=${this.saveName}
                  @change=${(e: CustomEvent<{ value: string }>) => (this.saveName = e.detail.value)}
                  @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.saveCurrentFilter()}
                ></vitesse-text-input>
                <button class="filter-add" ?disabled=${!validName(this.saveName)} @click=${() => this.saveCurrentFilter()}>
                  ${t('integration.filterSave')}
                </button>
              </span>`
            : html`<button class="filter-add" title=${t('integration.filterSaveHint')} @click=${() => (this.saveOpen = true)}>
                ${t('integration.filterSave')}
              </button>`
          : ''
      }
      ${saved.map(
        (f) => html`<span class="filter-saved" title=${t('integration.filterSavedHint', { n: f.predicates.length })}>
          <button class="filter-saved-apply" @click=${() => this.applySaved(f)}>${f.name}</button>
          <button
            title=${t('integration.filterSavedRemove')}
            @click=${() => this.writeSaved(removeFilter(this.saved, f.name, f.table))}
          >
            ✕
          </button>
        </span>`,
      )}
      ${this.totalRows !== undefined ? html`<span class="filter-count">${t('integration.filterRows', { n: formatNumber(this.totalRows) })}</span>` : ''}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-table-viewport': TableViewport;
  }
}
